import { find, get, isEmpty, map, reduce, round } from 'lodash-es';

import uuidv4 from 'uuid/v4';
import { checkIsEmpty } from '../../utils';
import { getDurationInMonths, convertDurationMonthsInUnit } from '../../../../Utils/Utils';

/**
 * get question
 * @param extraData
 */
const getQuestionGroup = extraData => ({
  code: get(extraData, 'code'),
  sequence: get(extraData, 'sequence'),
});

const handleQuestionGroup = group => {
  let questions = [];

  // from web service
  if (!isEmpty(get(group, 'extra_data'))) {
    questions = map(get(group, 'extra_data'), extraData => getQuestionGroup(extraData));
  }

  // from store
  if (!isEmpty(get(group, 'questions'))) {
    questions = [...questions, ...map(get(group, 'questions'), question => getQuestionGroup(question))];
  }

  return questions;
};

/**
 * get group
 * @param group
 */
const getGroup = group => ({
  id: !checkIsEmpty(get(group, 'id')) ? get(group, 'id') : uuidv4(),
  title: get(group, 'title'),
  description: get(group, 'description'),
  tooltip: get(group, 'tooltip'),
  sequence: get(group, 'sequence'),
  questions: handleQuestionGroup(group),
  // todo verify reel case
  groups: !isEmpty(get(group, 'groups')) ? map(get(group, 'groups'), groupItem => getGroup(groupItem)) : undefined,
});

/**
 * get question
 * @param {*} question
 */
const getQuestion = (question, answerValue) => {
  try {
    let validation = [];

    if (!isEmpty(get(question, 'validation'))) {
      validation = get(question, 'validation');
    }

    if (question.required) {
      validation.push({ required: true, message: ' ' /* 'champ obligatoire' */ }); // HACK demo to not display error message
    }

    const result = {
      ...question,
      value: answerValue,
      validation,
    };
    return result;
  } catch (e) {
    // eslint-disable-next-line no-console
    console.error(`getPart ${e.stack}`);
  }
  return undefined;
};

/**
 * get answer value
 * @param {} answersQuestion
 * @param {} questionItem
 */
const getAnswerValue = (answersQuestion, questionItem) => {
  if (get(questionItem, 'path.path')) {
    return get(answersQuestion, `${get(questionItem, 'path.path')}`);
  }

  return get(answersQuestion, questionItem.code);
};

/**
 * get part
 * @param part
 */
const getPart = (part, answersQuestion) => {
  try {
    const questions = map(get(part, 'questions'), questionItem => {
      const question = getQuestion(questionItem, getAnswerValue(answersQuestion, questionItem));
      return question;
    });
    const groups = !isEmpty(get(part, 'groups')) ? map(get(part, 'groups'), group => getGroup(group)) : undefined;
    const result = {
      ...part,
      questions,
      groups,
    };
    return result;
  } catch (e) {
    // eslint-disable-next-line no-console
    console.error(`getPart ${e.stack}`);
  }
  return undefined;
};

/**
 *
 * @param {*} answersParts
 * @param {*} part
 */
const getAnswersQuestions = (part, answersParts) => {
  try {
    const answersPart = find(answersParts, { id: get(part, 'id') });
    const answersQuestions = get(answersPart, 'answers');
    return answersQuestions;
  } catch (e) {
    // eslint-disable-next-line no-console
    console.error(`getAnswersQuestions ${e.stack}`);
  }
  return undefined;
};

/**
 * get block loan
 * @param block
 */
const getBlockLoan = (block, loan, answersParts = undefined) => {
  try {
    const parts = map(get(block, 'parts'), partItem => {
      const answersQuestions = answersParts ? getAnswersQuestions(partItem, answersParts) : undefined;
      const part = getPart(partItem, answersQuestions);
      return part;
    });

    const result = {
      ...block,
      id: get(loan, 'ref', 'loan1'),
      displayTitle: get(block, 'displayTitle', true),
      parts,
    };
    return result;
  } catch (e) {
    // eslint-disable-next-line no-console
    console.error(`getBlockLoan ${e.stack}`);
  }
  return undefined;
};

/**
 *  get answers loan
 * @param {*} loan
 */
const getAnswersLoans = loan => {
  try {
    if (loan) {
      const { ref, ...answers } = loan;

      // HACK for percent field that need to be between 0 and 1
      if (answers.rate != null) {
        answers.rate = `${round(parseFloat(answers.rate) * 100, 2)}`;
      }

      const parts = [{ id: 1, answers }];

      // check answers parts is empty
      if (isEmpty(parts)) {
        return undefined;
      }
      const answersLoan = { id: ref, parts };
      return answersLoan;
    }
  } catch (e) {
    // eslint-disable-next-line no-console
    console.error(e);
  }
  return undefined;
};

/**
 *
 * @param {*} param0
 */
const loansBlockTransform = ({ salesRoute, strings }) => {
  try {
    const block = get(salesRoute, 'questionnaires[0].description.loan_block');
    const graduatedLoanBlock = get(salesRoute, 'questionnaires[0].description.graduatedLoan_block');

    if (block) {
      // build generic block graduated loan
      const graduatedLoans = { generic: [getBlockLoan(graduatedLoanBlock, { ref: '1' })] };

      const blockLoans = map(get(salesRoute, 'loans', [{}]), (loan, idx) => {
        // Add duration to loan, because for graduated loans, API remove it...
        if (loan.kind === 'graduated') {
          const durationMonth = reduce(
            loan.increments,
            (acc, incr) =>
              acc +
              getDurationInMonths({
                duration: parseInt(incr.number_of_payments, 10),
                durationUnit: incr.payment_frequency,
              }),
            0
          );
          loan.duration = convertDurationMonthsInUnit({ duration: durationMonth, durationUnit: loan.duration_unit });
        }

        const answersLoans = getAnswersLoans(loan);
        const checkLoansHasAnswers = !isEmpty(get(answersLoans, 'parts'));

        // const isReady = questionnaireBlockIsReady({ questionnaire });

        // if (!isReady) {
        //   // eslint-disable-next-line no-console
        //   return {};
        // }

        // build block loan
        const loanBlock = getBlockLoan(block, loan, checkLoansHasAnswers ? get(answersLoans, 'parts') : null);

        // build block graduated loan
        graduatedLoans[loanBlock.id] = map(get(loan, 'increments', [{}]), (increment, idx) => {
          const incrWithref = { ...increment, ref: increment.id || `${idx + 1}` };
          const answersIncr = getAnswersLoans(incrWithref);
          const checkLoansHasAnswers = !isEmpty(get(answersIncr, 'parts'));

          return getBlockLoan(graduatedLoanBlock, incrWithref, checkLoansHasAnswers ? get(answersIncr, 'parts') : null);
        });

        return loanBlock;
      });

      return { loans: blockLoans, graduatedLoans, stringsBlock: block?.buttons };
    }
  } catch (e) {
    // eslint-disable-next-line no-console
    console.error(`questionnaireBlockTransform: ${e.stack}`);
  }

  return {};
};

export default loansBlockTransform;
